import React, { useState, useEffect, useRef } from 'react';
import { Grid } from 'semantic-ui-react';
import useTheme from '../../hooks/useTheme';

const FALLBACK_IMAGES = [
    "https://w0.peakpx.com/wallpaper/127/781/HD-wallpaper-cities-water-night-city-building-reflection-stockholm-sweden.jpg",
    "https://w0.peakpx.com/wallpaper/289/577/HD-wallpaper-cities-stockholm-sweden.jpg",
    "https://wallpaper.forfun.com/fetch/0b/0baab85b774046e7caea8b58d2636047.jpeg",
];

const FallbackImage = ({ src, alt, style, onClick, loading }) => {
    const [currentSrc, setCurrentSrc] = useState(src);
    const attemptRef = useRef(0);

    useEffect(() => {
        setCurrentSrc(src);
        attemptRef.current = 0;
    }, [src]);

    const handleError = () => {
        if (attemptRef.current < FALLBACK_IMAGES.length) {
            setCurrentSrc(FALLBACK_IMAGES[attemptRef.current]);
            attemptRef.current += 1;
        }
    };

    return (
        <img
            className="ui image"
            src={currentSrc}
            alt={alt}
            loading={loading}
            onClick={onClick}
            onError={handleError}
            style={style}
        />
    );
};

const PhotoGrid = ({ photos, onClick, title }) => {
    const theme = useTheme();

    const altText = (index = 1) => `${title ?? ''} - Photo ${index}`


    const renderPhotos = () => {
        if (photos.length === 1) {
            return (
                <Grid.Row >
                    <Grid.Column width={16}>
                        <FallbackImage
                            src={photos[0]}
                            alt={altText()}
                            loading='lazy'
                            onClick={() => {
                                onClick(photos[0])
                            }}
                            style={{
                                cursor: 'pointer',
                                borderRadius: theme.radius.default,
                                // 1200x675 aspect ratio
                                aspectRatio: '16/9',
                                objectFit: 'cover',
                                width: '100%',
                                maxHeight: '50vh',
                            }}
                        />
                    </Grid.Column>
                </Grid.Row>
            );
        } else if (photos.length === 2) {
            return (
                <Grid.Row >
                    {photos.map((photo, index) => (
                        <Grid.Column
                            key={index}
                            alt={altText(index)}
                            width={8}
                            style={{ padding: theme.spacing.elementPaddingSmall, }}
                        >
                            <FallbackImage
                                src={photo}
                                loading='lazy'
                                onClick={() => {
                                    onClick(photos[photo])
                                }}
                                style={{
                                    cursor: 'pointer',
                                    aspectRatio: '16/9',
                                    maxHeight: '40vh',
                                    width: '100%',
                                    borderRadius: theme.radius.default,
                                    objectFit: 'cover',
                                }}
                            />
                        </Grid.Column>
                    ))}
                </Grid.Row>
            );
        } else if (photos.length === 3) {
            return (
                <Grid>
                    <Grid.Row >
                        <Grid.Column width={11} style={{ padding: theme.spacing.elementPaddingSmall, }}>
                            <FallbackImage
                                loading='lazy'
                                alt={altText(1)}
                                src={photos[0]}
                                onClick={() => {
                                    onClick(photos[0])
                                }}
                                style={{
                                    cursor: 'pointer',
                                    width: '100%',
                                    objectFit: 'cover',
                                    aspectRatio: '16/8.5',
                                    borderRadius: theme.radius.default,
                                }}
                            />
                        </Grid.Column>
                        <Grid.Column width={5} style={{ padding: theme.spacing.elementPaddingSmall }}>
                            <FallbackImage
                                loading='lazy'
                                src={photos[1]}
                                alt={altText(2)}
                                onClick={() => {
                                    onClick(photos[1])
                                }}
                                style={{
                                    cursor: 'pointer',
                                    width: '100%',
                                    objectFit: 'cover',
                                    aspectRatio: '16/9',
                                    borderRadius: theme.radius.default,
                                }}
                            />
                            <FallbackImage
                                loading='lazy'
                                alt={altText(3)}
                                src={photos[2]}
                                onClick={() => {
                                    onClick(photos[2])
                                }}
                                style={{
                                    cursor: 'pointer',
                                    width: '100%',
                                    marginTop: theme.spacing.elementPadding,
                                    aspectRatio: '16/9',
                                    borderRadius: theme.radius.default,
                                    objectFit: 'cover',
                                }}
                            />
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            );
        } else if (photos.length === 4) {
            return (
                <Grid>
                    <Grid.Row >
                        <Grid.Column width={12} style={{ padding: theme.spacing.elementPaddingSmall, }}>
                            <FallbackImage
                                loading='lazy'
                                src={photos[0]}
                                alt={altText(1)}
                                onClick={() => {
                                    onClick(photos[0])
                                }}
                                style={{
                                    cursor: 'pointer',
                                    width: '100%',
                                    objectFit: 'cover',
                                    aspectRatio: '16/9.85',
                                    borderRadius: theme.radius.default,
                                }}
                            />
                        </Grid.Column>
                        <Grid.Column width={4} style={{ padding: theme.spacing.elementPaddingSmall }}>
                            <FallbackImage
                                loading='lazy'
                                alt={altText(2)}
                                src={photos[1]}
                                onClick={() => {
                                    onClick(photos[1])
                                }}
                                style={{
                                    cursor: 'pointer',
                                    width: '100%',
                                    objectFit: 'cover',
                                    aspectRatio: '16/9',

                                    borderRadius: theme.radius.default,
                                }}
                            />
                            <FallbackImage
                                loading='lazy'
                                alt={altText(3)}
                                src={photos[2]}
                                onClick={() => {
                                    onClick(photos[2])
                                }}
                                style={{
                                    cursor: 'pointer',
                                    width: '100%',
                                    marginTop: theme.spacing.elementPadding,
                                    aspectRatio: '1.55',
                                    borderRadius: theme.radius.default,
                                    objectFit: 'cover',
                                }}
                            />
                            <FallbackImage
                                loading='lazy'
                                src={photos[3]}
                                alt={altText(4)}
                                onClick={() => {
                                    onClick(photos[3])
                                }}
                                style={{
                                    cursor: 'pointer',
                                    width: '100%',
                                    marginTop: theme.spacing.elementPadding,
                                    aspectRatio: '16/9',
                                    borderRadius: theme.radius.default,
                                    objectFit: 'cover',
                                }}
                            />
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            );
        }
    };

    return <Grid>{renderPhotos()}</Grid>;
};

export default PhotoGrid;
